// Dashboard del módulo Catering. Catering → Resumen → Dashboard.
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, ChefHat, ClipboardList, Handshake, LayoutDashboard, Loader2,
  Percent, UtensilsCrossed, Truck, MapPin, Boxes, Utensils, Apple, Calculator, LayoutGrid, Gauge,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { num } from '@/features/inventario/shared';
import { useTabsStore } from '@/stores/tabs.store';
import { DIAS, OP_ESTADO_CLS, OP_ESTADO_LABEL, catRpc, money } from './shared';

interface DashKpis {
  recetas: number; platos: number; recetas_sin_costo: number; mp_sin_precio: number;
  contratos: number; pax_pactado_dia: number;
  // Base comparable: solo los días que tienen orden de producción
  dias_produccion: number; dias_efectivos: number; dias_sin_producir: number;
  pax_servidos: number; porciones_producidas: number;
  costo_mp_mes: number; ingreso_comparable: number;
  costo_por_servicio: number; costo_por_porcion: number; food_cost_pct: number;
}
interface DashData {
  kpis: DashKpis;
  ciclo_nombre: string | null;
  costo_menu_dia: { dia: number; items: number; costo: number; alertas: number }[];
  margen_contratos: { codigo: string; cliente: string; porciones: number; ingreso: number; costo: number; margen: number; margen_pct: number }[];
  ops_recientes: { id: string; codigo: string; fecha: string; estado: string; porciones_plan: number; porciones_reales: number; costo_mp: number; costo_porcion: number }[];
  top_costosas: { nombre: string; tipo: string; costo_porcion: number }[];
  mp_sin_precio_lista: string[];
}

interface GestionData {
  nutricion: { mp_total: number; mp_con_datos: number; cobertura_pct: number };
  dietas_hoy: number; dietas_tipos: number; licitaciones: number; ciclos: number; platos: number;
  capacidad?: { tiene_plan: boolean; rojo: number; ambar: number; verde: number; util_global_pct: string | number | null; cuello_estacion: string | null };
}
interface LogData {
  despachos_hoy: { total: number; borrador: number; cargado: number; en_ruta: number; cerrado: number };
  despachos_mes: number;
  entregas: { entregado: number; incidencia: number };
  entregas_pendientes: number;
  guias: { autorizado: number; pendiente: number; error: number };
  menaje: { clientes: number; unidades: number; valor: number };
  sobrantes: { despachadas: number; devueltas: number; pct: number };
}

export function DashboardCatering() {
  const openTab = useTabsStore((s) => s.openTab);
  const ir = (path: string, title: string) => openTab({ id: path, title, path });

  const { data, isFetching } = useQuery<DashData>({
    queryKey: ['cat-dashboard'],
    queryFn: () => catRpc<DashData>('cat_dashboard'),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: log } = useQuery<LogData>({
    queryKey: ['cat-logistica-resumen'],
    queryFn: () => catRpc<LogData>('cat_logistica_resumen'),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: gest } = useQuery<GestionData>({
    queryKey: ['cat-gestion-resumen'],
    queryFn: () => catRpc<GestionData>('cat_gestion_resumen'),
    staleTime: 0, refetchOnMount: 'always',
  });

  if (!data) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" /> Cargando dashboard…
      </div>
    );
  }
  const k = data.kpis;

  const tiles = [
    { icon: ChefHat, label: 'Recetas base', valor: num(k.recetas, 0), sub: `${num(k.platos, 0)} platos ensamblados` },
    { icon: Handshake, label: 'Contratos activos', valor: num(k.contratos, 0), sub: `${num(k.pax_pactado_dia, 0)} pax pactados/día` },
    { icon: UtensilsCrossed, label: 'Pax servidos', valor: num(k.pax_servidos, 0), sub: `${num(k.porciones_producidas, 0)} porciones producidas` },
    { icon: ClipboardList, label: 'Costo MP del mes', valor: money(k.costo_mp_mes, 2), sub: `${money(k.costo_por_servicio)} por servicio · ${money(k.costo_por_porcion)} por porción` },
    { icon: Percent, label: 'Food cost', valor: `${num(k.food_cost_pct, 1)}%`, sub: `sobre ${money(k.ingreso_comparable, 2)} facturables`, alerta: k.food_cost_pct > 38 },
    { icon: AlertTriangle, label: 'Costeo incompleto', valor: num(k.recetas_sin_costo, 0), sub: `recetas con MP sin precio (${num(k.mp_sin_precio, 0)} MP)`, alerta: k.recetas_sin_costo > 0 },
  ];

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <LayoutDashboard className="w-5 h-5" /> Dashboard de Catering
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {tiles.map((t) => (
          <Card key={t.label} className={t.alerta ? 'border-amber-400' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><t.icon className="w-4 h-4" /> {t.label}</div>
              <div className={`text-2xl font-semibold font-mono tabular-nums mt-1 ${t.alerta ? 'text-amber-600 dark:text-amber-400' : ''}`}>{t.valor}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {k.dias_sin_producir > 0 && (
        <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm">
          <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <span>
            El food cost y el margen se calculan sobre los <b>{num(k.dias_produccion, 0)} día(s) con orden de producción</b>,
            no sobre los {num(k.dias_efectivos, 0)} con pax registrados: {num(k.dias_sin_producir, 0)} día(s) tienen
            efectivos sin producir todavía y mezclarlos daría un food cost irreal.
          </span>
        </div>
      )}

      {log && (
        <div>
          <h2 className="flex items-center gap-2 pb-2 text-sm font-semibold text-muted-foreground"><Truck className="h-4 w-4" /> Logística del mes</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Truck className="h-4 w-4" /> Despachos hoy</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">{num(log.despachos_hoy.total, 0)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{log.despachos_hoy.en_ruta} en ruta · {log.despachos_mes} en el mes</div>
              </CardContent>
            </Card>
            <Card className={log.entregas_pendientes > 0 ? 'border-amber-400' : ''}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><MapPin className="h-4 w-4" /> Entregas (POD)</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">{num(log.entregas.entregado, 0)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{log.entregas.incidencia} incidencia(s){log.entregas_pendientes > 0 ? ` · ${log.entregas_pendientes} pendiente(s)` : ''}</div>
              </CardContent>
            </Card>
            <Card className={log.guias.error > 0 ? 'border-amber-400' : ''}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><ClipboardList className="h-4 w-4" /> Guías de remisión</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">{num(log.guias.autorizado, 0)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">autorizadas{log.guias.pendiente > 0 ? ` · ${log.guias.pendiente} en trámite` : ''}{log.guias.error > 0 ? ` · ${log.guias.error} con error` : ''}</div>
              </CardContent>
            </Card>
            <Card className={log.menaje.valor > 0 ? 'border-amber-400' : ''}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Boxes className="h-4 w-4" /> Menaje sin devolver</div>
                <div className={`mt-1 text-2xl font-semibold font-mono tabular-nums ${log.menaje.valor > 0 ? 'text-amber-600 dark:text-amber-400' : ''}`}>{money(log.menaje.valor, 2)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{log.menaje.valor > 0 ? `${num(log.menaje.unidades, 0)} unidades · ${log.menaje.clientes} cliente(s)` : 'todo devuelto'}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Utensils className="h-4 w-4" /> Sobrantes</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">{num(log.sobrantes.pct, 1)}%</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{num(log.sobrantes.devueltas, 0)} de {num(log.sobrantes.despachadas, 0)} raciones</div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {gest && (
        <div>
          <h2 className="flex items-center gap-2 pb-2 text-sm font-semibold text-muted-foreground"><LayoutDashboard className="h-4 w-4" /> Gestión y analítica</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Card className={gest.capacidad?.tiene_plan && gest.capacidad.rojo > 0 ? 'border-red-400' : ''}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Gauge className="h-4 w-4" /> Capacidad hoy</div>
                {gest.capacidad?.tiene_plan ? (
                  <>
                    <div className={`mt-1 text-2xl font-semibold tabular-nums ${gest.capacidad.rojo > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{num(gest.capacidad.rojo, 0)}</div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">{gest.capacidad.rojo > 0 ? `en cuello · ${gest.capacidad.cuello_estacion ?? ''}` : `sin cuellos · ${num(Number(gest.capacidad.util_global_pct) || 0, 0)}% MO`}</div>
                  </>
                ) : (
                  <>
                    <div className="mt-1 text-2xl font-semibold tabular-nums text-muted-foreground">—</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">sin plan de producción hoy</div>
                  </>
                )}
              </CardContent>
            </Card>
            <Card className={gest.nutricion.cobertura_pct < 80 ? 'border-amber-400' : ''}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Apple className="h-4 w-4" /> Cobertura nutricional</div>
                <div className={`mt-1 text-2xl font-semibold tabular-nums ${gest.nutricion.cobertura_pct < 80 ? 'text-amber-600 dark:text-amber-400' : ''}`}>{num(gest.nutricion.cobertura_pct, 0)}%</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{gest.nutricion.mp_con_datos}/{gest.nutricion.mp_total} MP con datos</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><ClipboardList className="h-4 w-4" /> Dietas hoy</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">{num(gest.dietas_hoy, 0)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{gest.dietas_tipos} tipos terapéuticos</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Calculator className="h-4 w-4" /> Licitaciones</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">{num(gest.licitaciones, 0)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">simulaciones guardadas</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><LayoutGrid className="h-4 w-4" /> Menús</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">{num(gest.platos, 0)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">platos · {gest.ciclos} ciclos</div>
              </CardContent>
            </Card>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {[
              ['/catering/capacidad', 'Plan de capacidad'],
              ['/catering/calibracion', 'Calibración de fichas'],
              ['/catering/pronostico', 'Pronóstico de pax'],
              ['/catering/ingenieria', 'Ingeniería de menú'],
              ['/catering/optimizador', 'Optimizador de menús'],
              ['/catering/licitacion', 'Simulador de licitación'],
              ['/catering/nutricion', 'Nutrición y dietas'],
              ['/catering/dietas', 'Censo de dietas'],
              ['/catering/haccp', 'Fichas y HACCP'],
            ].map(([ruta, titulo]) => (
              <button key={ruta} onClick={() => ir(ruta, titulo)} className="rounded-full border px-3 py-1 text-xs hover:bg-accent">{titulo}</button>
            ))}
          </div>
        </div>
      )}

      {k.mp_sin_precio > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Materias primas sin precio vigente: <b>{data.mp_sin_precio_lista.join(', ')}</b>.
            Todo menú que las use tiene el costo incompleto —{' '}
            <button className="font-medium underline" onClick={() => ir('/catering/recetas', 'Recetario')}>regístralas en el Recetario → Precios</button>.
          </span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Margen por contrato */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Margen por contrato (mes en curso)</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Contrato</th>
                <th className="py-2 pr-3 text-right">Porciones</th><th className="py-2 pr-3 text-right">Facturable</th>
                <th className="py-2 pr-3 text-right">Costo MP</th><th className="py-2 pr-3 text-right">Margen</th><th className="py-2 pr-3 text-right">%</th>
              </tr></thead>
              <tbody>
                {data.margen_contratos.map((m, i) => (
                  <tr key={m.codigo} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3"><span className="font-mono text-xs">{m.codigo}</span> {m.cliente}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(m.porciones, 0)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(m.ingreso, 2)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(m.costo, 2)}</td>
                    <td className={`py-2 pr-3 text-right font-mono tabular-nums ${m.margen < 0 ? 'text-red-600' : ''}`}>{money(m.margen, 2)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums text-muted-foreground">{num(m.margen_pct, 1)}%</td>
                  </tr>
                ))}
                {data.margen_contratos.length > 0 && (
                  <tr className="border-t-2 font-semibold bg-muted/30">
                    <td className="py-2 pr-3" colSpan={2}>Totales ({data.margen_contratos.length})</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(data.margen_contratos.reduce((a, b) => a + Number(b.porciones), 0), 0)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(data.margen_contratos.reduce((a, b) => a + Number(b.ingreso), 0), 2)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(data.margen_contratos.reduce((a, b) => a + Number(b.costo), 0), 2)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(data.margen_contratos.reduce((a, b) => a + Number(b.margen), 0), 2)}</td>
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
            <p className="pt-2 text-xs text-muted-foreground">
              Costo de MP prorrateado por porciones servidas. El prorrateo de indirectos por driver llega en una próxima fase.
            </p>
          </CardContent>
        </Card>

        {/* Costo del menú por día */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Costo del menú por día {data.ciclo_nombre ? `— ${data.ciclo_nombre}` : ''}</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Día</th><th className="py-2 pr-3 text-right">Ítems</th>
                <th className="py-2 pr-3 text-right">Costo Σ/porción</th><th className="py-2 pr-3 text-right">Alertas</th>
              </tr></thead>
              <tbody>
                {data.costo_menu_dia.map((d) => (
                  <tr key={d.dia} className="border-b last:border-0">
                    <td className="py-2 pr-3">{DIAS[d.dia - 1]}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(d.items, 0)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(d.costo, 3)}</td>
                    <td className="py-2 pr-3 text-right">
                      {Number(d.alertas) > 0
                        ? <span className="inline-flex items-center gap-1 text-xs text-amber-600"><AlertTriangle className="h-3 w-3" />{num(d.alertas, 0)}</span>
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
                {!data.costo_menu_dia.length && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Sin menú cíclico configurado</td></tr>}
              </tbody>
            </table>
            <p className="pt-2 text-xs text-muted-foreground">
              Comparar el costo entre días (y entre los menús de la rotación) permite rebalancear la rotación anual.
            </p>
          </CardContent>
        </Card>

        {/* OPs recientes */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Órdenes de producción recientes</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Orden</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Estado</th>
                <th className="py-2 pr-3 text-right">Porciones</th><th className="py-2 pr-3 text-right">Costo MP</th><th className="py-2 pr-3 text-right">$/porción</th>
              </tr></thead>
              <tbody>
                {data.ops_recientes.map((o) => (
                  <tr key={o.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-mono text-xs">{o.codigo}</td>
                    <td className="py-2 pr-3">{o.fecha}</td>
                    <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${OP_ESTADO_CLS[o.estado]}`}>{OP_ESTADO_LABEL[o.estado]}</span></td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(o.porciones_reales || o.porciones_plan, 0)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(o.costo_mp, 2)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(o.costo_porcion)}</td>
                  </tr>
                ))}
                {!data.ops_recientes.length && (
                  <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">
                    Sin órdenes — <button className="text-primary underline" onClick={() => ir('/catering/produccion', 'Producción del día')}>genera la primera</button>
                  </td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Top recetas costosas */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Recetas más caras por porción</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Receta</th>
                <th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3 text-right">Costo/porción</th>
              </tr></thead>
              <tbody>
                {data.top_costosas.map((r, i) => (
                  <tr key={r.nombre} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3">{r.nombre}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{r.tipo === 'plato' ? 'Plato' : 'Receta base'}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.costo_porcion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="pt-2 text-xs text-muted-foreground">
              Insumo para la ingeniería de menú: qué platos revisar (porción, sustitución de ingrediente o rotación).
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
